// 店舗オーナー・スタッフの運営操作 API (#1138): store-auth/スタッフ管理/返金/
// Stripe Connect/dispute evidence/geocode/メニューOCR。配置ルールは index.ts 参照。
import {
  maybeShowQuotaWarning,
  parseQuotaExceeded,
  showQuotaExceededToast,
} from '../aiQuotaToast'
import { API_BASE, ApiError, _staffSessionToken, apiFetch, getFirebaseIdToken, trackedFetch } from './_client'
import type { MenuSaleLine } from '../../lib/menuSale'
import type { CounterOrderState, UnsettledCounterOrder } from '../../lib/counterOrderState'
import type { VariationSnapshot } from '../../lib/menuVariation'
import { getDeviceId } from '../../lib/deviceId'
import type { ReceiptBlock } from '../../components/ReceiptPreview'  // #1880 レシートプレビュー
import type { RegisterPayMethod } from '../../lib/storePayment'


// #1460 閲覧の集計。**匿名カウンタなので「転換率」ではない** — 閲覧と売上は
// 並べて見せるものであって、見た人と買った人は紐付いていない。
/** #2308 ③ R10 オーナーがオンラインで返せる方法 */
export type RefundOnlineMethods = { paypay: boolean; paypal: boolean; bankTransfer: true }

/** #1683 SaaS 利用料の未払いによる休会。`/store-auth/me` の `suspension` と同じ形。 */
export type OwnerSuspensionState = {
  suspended: boolean
  suspendedAt?: string | null
  /** 社内が入力した理由 (「8月分未入金」等)。**オーナー本人にだけ**出す。
   *  顧客向けの経路では休会と臨時休業を区別しない (店の信用を守るため)。 */
  reason?: string | null
}

/** #1657 加盟店規約の同意状態。`/store-auth/me` の `terms` と同じ形。 */
export type MerchantTermsState = {
  currentVersion: string
  agreedVersion: string | null
  agreedAt: string | null
  /** 版が一致しない = 未同意。初回も true */
  needsAgreement: boolean
  /** 「最後まで読んだ上での明示的なタップ」を求めるか。初回、または同意済みの版から現行版の間に不利益変更があるとき (#1736) */
  requiresExplicitConsent: boolean
  /** #1736 前回同意した版より後の改定 (古い順)。初回は空。ゲートで先に見せる */
  changesSinceAgreed?: { version: string; date: string; summary: string; disadvantageous: boolean }[]
}

export type MerchantTerms = MerchantTermsState & {
  /** 規約本文 (markdown)。表示したものと同意記録のハッシュが一致する */
  text: string
  contentHash: string
}

/** #1710 EC 出店規約 (店舗オーナー認証付き)。同意欄は加盟店規約と別。 */
export type EcVendorTerms = MerchantTerms & {
  /** このオーナーの店舗のどれかが EC の出店者としてリンクされているか。false なら同意を求めない */
  applicable: boolean
}

/** #1710 EC 出店規約の本文 (auth 不要。弁護士・出店者の確認用。MoU は出さない — 個別締結の条件) */
export type EcVendorTermsPublic = {
  terms: { text: string; version: string; contentHash: string }
}

export type ViewCounts = {
  view: number
  favorite: number
  add_to_cart: number
  purchase: number
}

export type ViewMetricsSummary = {
  storeId: string
  from: string
  to: string
  total: ViewCounts
  /** PAY 本体 (店舗詳細) の分 */
  pay: ViewCounts
  /** EC の分。EC に出していない店は 0 になる */
  ec: ViewCounts
  daily: (ViewCounts & { date: string })[]
  /** #1467 直前の同じ長さの期間 */
  previous: ViewCounts
  previousFrom: string
  previousTo: string
  /** 'ok' のときだけ比較を出す。'before_measurement' は計測開始前で比較不能 */
  comparison: 'ok' | 'before_measurement' | 'no_data'
}

/** #2004 町 = (都道府県, 市区町村)。マスタにある表記だけ (定義はサーバの services/view_metrics.py)。
 * 市の名前だけを鍵にしない — 府中市・伊達市は 2 つの県にある。 */
export type CityPlace = { prefecture: string; city: string }

/** #1991 / #2004 人数のマス。**null = 少人数のため出さない** (0 人のときも null — 0 と 1〜4 を
 * 出し分けると「1 人以上いる」が分かってしまうため)。
 * 隠すのはサーバ (services/small_counts.py)。画面で実数から隠しているのではない。 */
export type CityReachCounts = {
  /** 基準の人数 (使った人 / 住んでいる人) */
  customers: number | null
  /** うち、通知を許可した端末を持つ人 (注文・予約の通知が届く) */
  pushSubscribed: number | null
  /** うち、お知らせの受信をオンにした人 (自治体のイベント告知が実際に届く数) */
  announcementsOptIn: number | null
  /** うち、メールアドレスがあり、地域のお知らせメールを停止していない人 (#2004) */
  mailReachable: number | null
}

/** 1 つの町 (または「その他」「全体」) の 2 つの列。 */
export type CityReachPair = {
  /** その町の店で支払ったことがある登録者 (行動の事実。期間を問わない) */
  used: CityReachCounts
  /** 居住地にその町を選んだ登録者 (本人の自己申告。任意) */
  residents: CityReachCounts
}

export type CityReachSummary = {
  prefecture: string | null
  city: string | null
  /** true = 少人数を隠した結果 (見せる用)。false = 実数 (管理者の内部確認) */
  suppressed: boolean
  /** これ未満の人数は出さない */
  threshold: number
  generatedAt: string
  cities: (CityPlace & CityReachPair)[]
  /** 絞り込みの選択肢 (名前だけ)。店がまだ無い町も、住んでいる人が出せる人数なら入る */
  availablePlaces: CityPlace[]
  /** 少人数の町の合計。町の名前も数も持たない。1 つの町に絞っている間・実数の間は null */
  other: CityReachPair | null
  /** 重複なしの全体。1 つの町に絞っている間は null (他の町の数字を混ぜない) */
  total: CityReachPair | null
  /** 「住んでいる人」の分母: 登録者数と、そのうち居住地を回答した人数 */
  registered: number | null
  answered: number | null
}

export type CityMetricsSummary = {
  from: string
  to: string
  /** 絞り込み中の市区町村。null なら全体 */
  prefecture: string | null
  city: string | null
  /** 絞り込みタブの選択肢。名前だけで数字は含まない */
  availableCities: CityPlace[]
  cities: (ViewCounts & CityPlace & {
    revenue: number
    visitors: number
    /** #1467 直前の同じ長さの期間の閲覧数 */
    previousView: number
  })[]
  stores: (ViewCounts & CityPlace & {
    storeId: string
    name: string
    revenue: number
    visitors: number
    /** 閉店（soft-delete）済み。数字は期間の事実として残す */
    deleted?: boolean
  })[]
  /** 町を特定できず集計に入らなかった分。0 でなければ店舗データの穴 */
  unassigned: ViewCounts
  /** その店の一覧 (住所が未設定・表記がマスタと合わない)。管理者が直すためのもの */
  unresolvedStores: { storeId: string; name: string; prefecture: string; city: string }[]
  previousFrom: string
  previousTo: string
  comparison: 'ok' | 'before_measurement' | 'no_data'
}

// #742: dispute evidence ファイル category (= Stripe Dispute Evidence file 系 field 名そのまま)
export type DisputeEvidenceCategory =
  | 'receipt'
  | 'customer_signature'
  | 'customer_communication'
  | 'service_documentation'
  | 'shipping_documentation'
  | 'cancellation_policy_disclosure'
  | 'duplicate_charge_documentation'
  | 'refund_policy_disclosure'
  | 'uncategorized_file'

export type DisputeEvidenceFile = {
  id?: string
  stripeFileId: string
  category: DisputeEvidenceCategory
  filename?: string
  mimeType?: string
  size?: number
  uploadedAt?: unknown
}

// #736: dispute evidence response shape (= disputeId 起点 / paymentIntentId 起点で同じ)
export type DisputeEvidenceResponse = {
  /** #1744 対象決済のカード返金が Stripe 側で失敗していたときの要点 (無ければ null)。
   *  altMethod: 'none' = 返金なし (システム自動記録) / 'cash' = その場で現金で返した */
  refundFailed?: { amountJpy: number; requestedAt?: string | null; failureReason?: string | null; altMethod?: string | null } | null
  dispute: {
    id?: string
    disputeId?: string
    storeId?: string
    chargeId?: string
    paymentIntentId?: string
    stripeAccountId?: string
    amount?: number
    currency?: string
    reason?: string
    status?: string
    evidenceDueBy?: number
    /** #1651 この申立てで Stripe が引いた手数料の実額 (税込)。固定値は持たない */
    feeJpy?: number
    /** Stripe に反証を提出した回数。1 以上なら受け入れ (close) はもうできない */
    submissionCount?: number
    /** #1718 自動提出した日時。webhook が submissionCount を書き戻す前の同じ状態 */
    autoSubmittedAt?: string
  } | null
  evidence: {
    disputeId?: string
    transaction?: { amount?: number; customerIp?: string; customerUserAgent?: string; cardFingerprint?: string }
    webauthnAuthLogs?: { credentialId?: string; signCount?: number; ip?: string; userAgent?: string; authenticatedAt?: unknown }[]
    reservation?: { id?: string; courseName?: string; date?: string; time?: string; partySize?: number; status?: string; totalAmount?: number; contactName?: string }
    orders?: { id: string; data: Record<string, unknown> }[]
    receipt?: { url?: string; stripeStatus?: string; threeDSecure?: unknown }
    collectedAt?: unknown
  } | null
}

export interface StoreRegistrationPayload {
  name: string
  nameEn: string
  /** #1661 飲食の細分類。**非飲食の店は送らない**（業態は genre）。 */
  category?: string
  address: string
  prefecture?: string
  city?: string
  building?: string  // #696: 建物名・部屋番号 (任意)
  areaTags?: string[]
  hours: string
  imageUrl: string
  latitude: number
  longitude: number
  // #721: 任意の Google Maps URL。設定すると顧客側 InfoTab の「Google Maps で開く」が
  // この URL に直リンク化され、店舗の Google ビジネスプロフィール (= レビュー / 写真)
  // を表示できる。未設定なら住所テキスト search に fallback。
  googleMapsUrl?: string | null
  menu: { name: string; nameEn: string; price: number; imageUrl: string; description: string }[]
}

export const storeOwnerApi = {
  // #736 Dispute Evidence 自動収集 — 加盟店 dashboard で preview + Stripe submit
  getDisputeEvidence: async (disputeId: string): Promise<DisputeEvidenceResponse> => {
    const res = await apiFetch(`/dispute-evidence/${encodeURIComponent(disputeId)}`, { method: 'GET' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to load dispute evidence')
    return res.json() as Promise<DisputeEvidenceResponse>
  },
  submitDisputeEvidence: async (disputeId: string, customEvidence?: Record<string, unknown>) => {
    const res = await apiFetch(`/dispute-evidence/${encodeURIComponent(disputeId)}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customEvidence }),
    })
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      throw new ApiError(res.status, detail || 'Failed to submit evidence')
    }
    return res.json() as Promise<{ stripeStatus: string; submittedAt: string }>
  },
  // #736: 既存 URL `/store/disputes/:storeId/:paymentIntentId` 互換のため、PI 起点で
  // dispute + evidence を引く。サーバ側で逆引き → ownership check。
  getDisputeEvidenceByPaymentIntent: async (paymentIntentId: string) => {
    const res = await apiFetch(
      `/dispute-evidence/by-payment-intent/${encodeURIComponent(paymentIntentId)}`,
      { method: 'GET' },
    )
    if (!res.ok) throw new ApiError(res.status, 'Failed to load dispute evidence by PI')
    return res.json() as Promise<DisputeEvidenceResponse>
  },
  submitDisputeEvidenceByPaymentIntent: async (
    paymentIntentId: string,
    customEvidence?: Record<string, unknown>,
  ) => {
    const res = await apiFetch(
      `/dispute-evidence/by-payment-intent/${encodeURIComponent(paymentIntentId)}/submit`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customEvidence }),
      },
    )
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      throw new ApiError(res.status, detail || 'Failed to submit evidence')
    }
    return res.json() as Promise<{ stripeStatus: string; submittedAt: string }>
  },

  /**
   * #1718 売上取消請求を受け入れる（争わない）。**取り消せない。**
   *
   * これが無いと「受け入れたい」と「気づいていない」が区別できず、自動提出が
   * 店の落ち度が明らかな場合にも反論してしまう。
   */
  acceptDispute: async (disputeId: string): Promise<{ status: string }> => {
    const res = await apiFetch(`/dispute-evidence/${disputeId}/accept`, {
      method: 'POST',
    })
    return res.json()
  },
  // #742: dispute evidence ファイル添付 (= client-side 圧縮済み画像 / PDF を upload)
  listDisputeEvidenceFiles: async (disputeId: string) => {
    const res = await apiFetch(
      `/dispute-evidence/${encodeURIComponent(disputeId)}/files`,
      { method: 'GET' },
    )
    if (!res.ok) throw new ApiError(res.status, 'Failed to list files')
    return res.json() as Promise<{ files: DisputeEvidenceFile[] }>
  },
  uploadDisputeEvidenceFile: async (
    disputeId: string,
    category: DisputeEvidenceCategory,
    file: Blob,
    filename: string,
  ) => {
    const fd = new FormData()
    fd.append('category', category)
    fd.append('file', file, filename)
    const res = await apiFetch(
      `/dispute-evidence/${encodeURIComponent(disputeId)}/files`,
      { method: 'POST', body: fd },
    )
    if (!res.ok) {
      const detail = await res.text().catch(() => '')
      throw new ApiError(res.status, detail || 'Failed to upload evidence file')
    }
    return res.json() as Promise<DisputeEvidenceFile>
  },
  deleteDisputeEvidenceFile: async (disputeId: string, stripeFileId: string) => {
    const res = await apiFetch(
      `/dispute-evidence/${encodeURIComponent(disputeId)}/files/${encodeURIComponent(stripeFileId)}`,
      { method: 'DELETE' },
    )
    if (!res.ok) throw new ApiError(res.status, 'Failed to delete file')
    return res.json() as Promise<{ deleted: boolean }>
  },

  // Store Dashboard
  getStoreStats: async (storeId: string) => {
    const res = await apiFetch(`/store-dashboard/${storeId}/stats`)
    return res.json()
  },

  getStoreReport: async (storeId: string, period: 'today' | 'week' | 'month' | 'year' = 'week') => {
    const res = await apiFetch(`/store-dashboard/${storeId}/report?period=${period}`)
    return res.json()
  },

  // Store Owner Auth
  storeAuthRegister: async (email: string, password: string, ownerName?: string, companyName?: string) => {
    const res = await apiFetch('/store-auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, ownerName: ownerName || '', companyName: companyName || '' }),
    })
    return res.json()
  },

  storeAuthGoogleInit: async (idToken: string) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/google-init`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to initialize Google account')
    return res.json()
  },

  storeAuthMe: async (idToken: string) => {
    // #1944 端末 ID を渡す。新端末ログインの検知をここで行うため。
    // **/store-devices/register だけでは足りない** — あちらは明示ログイン時に
    // しか呼ばれず、セッション復元や盗まれたトークンでは通らない。/me は
    // ダッシュボードが必ず叩くので、そこで取りこぼしを塞ぐ。
    // 送れない端末 (localStorage 不可) では header を付けず、従来どおり動く。
    let deviceId = ''
    try {
      deviceId = getDeviceId()
    } catch {
      /* private mode 等。検知が効かないだけで /me は成立させる */
    }
    const res = await trackedFetch(`${API_BASE}/store-auth/me`, {
      headers: {
        'Authorization': `Bearer ${idToken}`,
        ...(deviceId ? { 'X-Device-Id': deviceId } : {}),
      },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to get owner info')
    return res.json()
  },

  /** #1683 自分が休会中か。オーナーは休会中もログインできるので、
   *  何が止まっているかと解除の条件を画面で説明する必要がある。
   *
   *  `/me` ではなく専用の口を叩く。`/me` は custom claim 同期・新端末ログイン
   *  検知 (メール送信あり)・店舗一覧の取得までやるので、バナーのために店舗画面へ
   *  遷移するたび走らせるには重すぎる。 */
  // #2308 ③ R10 オーナーがオンラインで返せる方法 (銀行振込は常に。PayPay・PayPal は任意)
  getRefundOnlineMethods: async (): Promise<RefundOnlineMethods> => {
    const res = await apiFetch('/store-auth/refund-online-methods')
    return await res.json() as RefundOnlineMethods
  },
  setRefundOnlineMethods: async (methods: { paypay: boolean; paypal: boolean }): Promise<RefundOnlineMethods> => {
    const res = await apiFetch('/store-auth/refund-online-methods', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(methods),
    })
    return await res.json() as RefundOnlineMethods
  },

  getOwnerSuspension: async (): Promise<OwnerSuspensionState> => {
    const res = await apiFetch('/store-auth/suspension')
    if (!res.ok) throw new ApiError(res.status, 'Failed to load owner status')
    return await res.json() as OwnerSuspensionState
  },

  // ---- #1657 加盟店規約のアプリ内表示・同意 ----

  /** 規約の本文と同意状態。**本文は API が配信する** — 同意記録に残すハッシュを
   * サーバ自身が計算する必要があるため (client 送信のハッシュは証拠にならない)。 */
  getMerchantTerms: async (): Promise<MerchantTerms> => {
    const res = await apiFetch('/store-auth/terms')
    if (!res.ok) throw new ApiError(res.status, 'Failed to load merchant terms')
    return res.json()
  },

  agreeMerchantTerms: async (version: string): Promise<{ agreed: boolean; version: string; agreedAt: string }> => {
    const res = await apiFetch('/store-auth/terms/agree', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ version }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to record terms agreement')
    return res.json()
  },

  // ---- #1710 EC 出店規約 (加盟店規約と同じ建付け。同意欄は別) ----
  getEcVendorTerms: async (): Promise<EcVendorTerms> => {
    const res = await apiFetch('/store-auth/ec-terms')
    if (!res.ok) throw new ApiError(res.status, 'Failed to load EC vendor terms')
    return res.json()
  },

  agreeEcVendorTerms: async (version: string): Promise<{ agreed: boolean; version: string; agreedAt: string }> => {
    const res = await apiFetch('/store-auth/ec-terms/agree', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ version }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to record EC vendor terms agreement')
    return res.json()
  },

  /** auth 不要。ログインしていない弁護士・出店者が Web 上で読むための本文 */
  getEcVendorTermsPublic: async (): Promise<EcVendorTermsPublic> => {
    const res = await apiFetch('/ec/vendor-terms')
    if (!res.ok) throw new ApiError(res.status, 'Failed to load EC vendor terms')
    return res.json()
  },

  // #1641 スタッフが注文をリアルタイムで読むための Firebase カスタムトークン。
  // PIN ログイン中の端末だけが取れる (無効化されたスタッフには出ない)。
  getStaffRealtimeToken: async (storeId: string) => {
    const res = await apiFetch(`/store-staff/${encodeURIComponent(storeId)}/realtime-token`, {
      method: 'POST',
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to get staff realtime token')
    return res.json() as Promise<{ customToken: string; uid: string }>
  },

  // ---- #995 スタッフ名簿(owner・apiFetch で owner Firebase token 自動添付) ----
  listStaffMembers: async (storeId: string) => {
    const res = await apiFetch(`/store-staff/${storeId}/members`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to list staff')
    return res.json()
  },
  createStaffMember: async (storeId: string, body: { name: string; role: string; pin: string; experienceProductIds?: string[]; phone?: string }) => {
    const res = await apiFetch(`/store-staff/${storeId}/members`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to create staff')
    return res.json()
  },
  updateStaffMember: async (storeId: string, memberId: string, updates: { name?: string; role?: string; active?: boolean; pin?: string; experienceProductIds?: string[]; phone?: string }) => {
    const res = await apiFetch(`/store-staff/${storeId}/members/${memberId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update staff')
    return res.json()
  },
  getStaffCode: async (storeId: string) => {
    const res = await apiFetch(`/store-staff/${storeId}/code`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to get staff code')
    return res.json()
  },
  regenerateStaffCode: async (storeId: string) => {
    const res = await apiFetch(`/store-staff/${storeId}/code/regenerate`, { method: 'POST' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to regenerate staff code')
    return res.json()
  },
  // ---- 公開（コード必須・PIN不要 / PINログイン） ----
  staffMembersByCode: async (code: string) => {
    const res = await trackedFetch(`${API_BASE}/store-staff/members?code=${encodeURIComponent(code)}`)
    if (!res.ok) throw new ApiError(res.status, 'Invalid staff code')
    return res.json()
  },
  staffLogin: async (code: string, memberId: string, pin: string) => {
    const res = await trackedFetch(`${API_BASE}/store-staff/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, memberId, pin }),
    })
    if (!res.ok) {
      // #1011 ロック(429)時は残り秒を拾って UI で「あと○分」表示に使う。
      // #1683 休会(403)時は code='suspended' を拾う。**PIN 間違いと同じ扱いにすると
      // スタッフが打ち直し続けて PIN ロックまで踏み、オーナーに話が伝わらない。**
      let retryAfterSeconds: number | undefined
      let code: string | undefined
      if (res.status === 429 || res.status === 403) {
        try {
          const body = await res.json()
          const secs = body?.detail?.retryAfterSeconds
          if (typeof secs === 'number') retryAfterSeconds = secs
          if (typeof body?.detail?.code === 'string') code = body.detail.code
        } catch { /* body 無し/非JSON は無視 */ }
      }
      throw new ApiError(res.status, 'Login failed', code, retryAfterSeconds)
    }
    return res.json()
  },

  // #959 (管理番号_009/010) 決済音を per-owner で保存。soundId='' でオフ。
  storeAuthUpdatePaymentSound: async (idToken: string, paymentSoundId: string) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/payment-sound`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentSoundId }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update payment sound')
    return res.json()
  },

  // #962 (管理番号_009) 店舗サウンド音量 (0–1) を per-owner で保存（注文音・提供音）。
  storeAuthUpdateSoundVolume: async (idToken: string, volume: number) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/sound-volume`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ volume }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update sound volume')
    return res.json()
  },

  // #1025 本日のリアルタイム売上。以前は Firestore 公開 doc の onSnapshot だったが、
  // 誰でも全店の売上を読めたため API (VIEW_SALES) に寄せた。403 = 閲覧権限なし
  // (既定でバイト不可。店舗設定 rolePolicy で許可できる)。
  getStoreStatsToday: async (storeId: string) => {
    const res = await apiFetch(`/store-dashboard/${storeId}/stats-today`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to get today stats')
    return res.json() as Promise<Record<string, unknown>>
  },

  // #995 取引履歴は owner|manager。apiFetch でオーナー or スタッフ(店長)トークンを自動添付。
  storeAuthStoreTransactions: async (storeId: string) => {
    const res = await apiFetch(`/store-auth/stores/${storeId}/transactions`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to get store transactions')
    return res.json() as Promise<{ transactions: Array<Record<string, unknown>> }>
  },

  // #1167 自動印刷フックが polling する「直近15分の決済」一覧 (ALL_ROLES)。
  // #2152 settlements: 支払い 1 回につき 1 枚刷る印。卓会計の支払いも返り、orderIds が付く
  // (付けない古い呼び方は、卓会計を注文ごとに刷る前提で、注文の付いた支払いを返さない)
  storeAuthPendingReceipts: async (storeId: string, opts: { settlements?: boolean } = {}) => {
    const qs = opts.settlements ? '?settlements=1' : ''
    const res = await apiFetch(`/store-auth/stores/${storeId}/receipts/pending${qs}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to get pending receipts')
    return res.json() as Promise<{
      transactions: Array<{ id: string; amountJpyc: number; createdAt: string; orderIds?: string[] }>
    }>
  },

  // #1167 QR単発決済のレシート (ESC/POS)。copy=merchant で「店控え」表記。
  storeAuthReceiptPreview: async (storeId: string, transactionId: string, copy: 'customer' | 'merchant' = 'customer') => {
    const res = await apiFetch(`/store-auth/stores/${storeId}/receipts/${transactionId}/preview?copy=${copy}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to get receipt preview')
    return res.json() as Promise<{
      transactionId: string
      copy: string
      preview: string
      escposBase64: string
      escposBytes: number
    }>
  },

  // #1880 見本の会計でレシートを組んで返す — **紙に出さずに確認する**ための口。
  // 実在の取引が要らないので、店舗設定でメッセージや QR を変えたその場で見られる。
  // blocks は ESC/POS のバイト列と同じ呼び出しから出た記録 (ReceiptPreview が描く)。
  storeAuthReceiptSamplePreview: async (
    storeId: string,
    sample?: string,
    /** 保存前の紙幅で試し刷りするための上書き (保存はされない) */
    paperWidth?: number,
  ) => {
    const q = new URLSearchParams()
    if (sample) q.set('sample', sample)
    if (paperWidth) q.set('paperWidth', String(paperWidth))
    const qs = q.toString() ? `?${q}` : ''
    const res = await apiFetch(`/store-auth/stores/${storeId}/receipt-preview${qs}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to get receipt preview')
    return res.json() as Promise<{
      sample: string
      sampleChoices: Array<{ id: string; label: string }>
      /** 画面で見ているものと同じ紙が出るバイト列 */
      escposBase64: string
      /** 紙幅を桁数にしたもの (80mm=48 / 58mm=32) */
      width: number
      paperWidthMm: number
      /** 1 枚あたりの紙の長さの目安 (mm)。長さは設定ではなく中身の結果 */
      estimatedLengthMm: number
      blocks: ReceiptBlock[]
      preview: string
    }>
  },

  // #1881 レシートのロゴ。**2 値化した結果**を保存する (変換はブラウザ側)。
  // 紙幅ごとに 1 枚ずつ持つ — ロゴは紙幅いっぱいのドット数で持つものなので、
  // 1 枚では 80mm と 58mm の両方をまかなえない。
  storeAuthGetReceiptLogo: async (storeId: string) => {
    const res = await apiFetch(`/store-auth/stores/${storeId}/receipt-logo`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to get receipt logo')
    return res.json() as Promise<{
      configured: boolean
      sourceUrl?: string | null
      threshold?: number | null
      crop?: { heightDots: number; zoom: number; offsetX: number; offsetY: number } | null
      /** 店名との間隔（ドット。8 ドット = 1mm） */
      gapDots?: number | null
      variants?: Record<string, { width: number; height: number; data: string }>
    }>
  },

  storeAuthPutReceiptLogo: async (
    storeId: string,
    body: {
      sourceUrl?: string | null
      threshold?: number | null
      crop?: { heightDots: number; zoom: number; offsetX: number; offsetY: number } | null
      gapDots?: number
      variants: Record<string, { width: number; height: number; data: string }>
    },
  ) => {
    const res = await apiFetch(`/store-auth/stores/${storeId}/receipt-logo`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to save receipt logo')
    return res.json() as Promise<{ ok: boolean; variants: string[] }>
  },

  storeAuthDeleteReceiptLogo: async (storeId: string) => {
    const res = await apiFetch(`/store-auth/stores/${storeId}/receipt-logo`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to delete receipt logo')
    return res.json() as Promise<{ ok: boolean }>
  },

  // #1207 決済完了画面のデジタルレシート QR 用: 公開レシート URL の素材
  // (HMAC トークン) を返す。ALL_ROLES。
  storeAuthReceiptPublicLink: async (storeId: string, transactionId: string) => {
    const res = await apiFetch(
      `/store-auth/stores/${storeId}/receipts/${transactionId}/public-link`,
    )
    if (!res.ok) throw new ApiError(res.status, 'Failed to get receipt public link')
    return res.json() as Promise<{ transactionId: string; path: string; token: string }>
  },

  // #1208 店頭発行の紙領収書 (宛名・但し書き付き)。空欄は手書き用の下線印字。
  storeAuthRyoshushoPreview: async (
    storeId: string,
    transactionId: string,
    opts: { addressee?: string; purpose?: string } = {},
  ) => {
    const q = new URLSearchParams()
    if (opts.addressee) q.set('addressee', opts.addressee)
    if (opts.purpose) q.set('purpose', opts.purpose)
    const qs = q.toString()
    const res = await apiFetch(
      `/store-auth/stores/${storeId}/receipts/${transactionId}/ryoshusho-preview${qs ? `?${qs}` : ''}`,
    )
    if (!res.ok) throw new ApiError(res.status, 'Failed to get ryoshusho preview')
    return res.json() as Promise<{
      transactionId: string
      issuedCount: number
      reissue: boolean
      preview: string
      escposBase64: string
      escposBytes: number
    }>
  },

  // #1208 紙領収書の印刷成功を報告 (発行回数 +1 — 2回目以降は「再発行」印字)。
  storeAuthRyoshushoMarkIssued: async (storeId: string, transactionId: string) => {
    const res = await apiFetch(
      `/store-auth/stores/${storeId}/receipts/${transactionId}/ryoshusho-issued`,
      { method: 'POST' },
    )
    if (!res.ok) throw new ApiError(res.status, 'Failed to mark ryoshusho issued')
    return res.json() as Promise<{ transactionId: string; issuedCount: number }>
  },

  // ---- レジ会計オペレーション #1178 ----
  // 会計開始 (卓ロック+伝票印刷) / 取消 / レジ会計画面 / 現金受領。全て店員系
  // (apiFetch がオーナー token or スタッフセッションを自動添付)。

  registerCheckoutRequest: async (storeId: string, tableNumber: string) => {
    const res = await apiFetch('/register/checkout-request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber }),
    })
    return res.json() as Promise<{ tableNumber: string; amount: number; orderCount: number }>
  },

  registerCheckoutCancel: async (storeId: string, tableNumber: string) => {
    const res = await apiFetch('/register/checkout-cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber }),
    })
    return res.json()
  },

  // #1393 退店 — 卓を空ける。支払いでは卓を空けず、この操作だけが空ける。
  registerTableClear: async (storeId: string, tableNumber: string) => {
    const res = await apiFetch('/register/table-clear', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to clear the table')
    return res.json() as Promise<{ tableNumber: string; cleared: number }>
  },

  // #1460 店舗の閲覧数 (売上レポートに並べて出す)。owner|manager のみ。
  getStoreViewMetrics: async (storeId: string, from: string, to: string) => {
    const res = await apiFetch(
      `/metrics/store/${storeId}?from=${from}&to=${to}`,
    )
    if (!res.ok) throw new ApiError(res.status, 'Failed to load view metrics')
    return res.json() as Promise<ViewMetricsSummary>
  },

  // #1462 市区町村ごとの閲覧数と売上 (自治体向け)。管理者のみ。
  getCityViewMetrics: async (from: string, to: string, place?: CityPlace) => {
    const q = new URLSearchParams({ from, to })
    if (place) { q.set('city', place.city); q.set('prefecture', place.prefecture) }
    const res = await apiFetch(`/metrics/cities?${q.toString()}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to load city metrics')
    return res.json() as Promise<CityMetricsSummary>
  },

  /** #1991 / #2004 市区町村ごとの「使った人」と「住んでいる人」。期間は問わない。
   * `raw` は管理者の内部確認用の実数。既定は少人数を隠した結果。 */
  getCityReach: async (place?: CityPlace, raw = false) => {
    const q = new URLSearchParams()
    if (place) { q.set('city', place.city); q.set('prefecture', place.prefecture) }
    if (raw) q.set('raw', 'true')
    const qs = q.toString()
    const res = await apiFetch(`/metrics/cities/reach${qs ? `?${qs}` : ''}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to load city reach')
    return res.json() as Promise<CityReachSummary>
  },

  registerTableBill: async (storeId: string, tableNumber: string) => {
    const res = await apiFetch(
      `/register/table-bill?storeId=${storeId}&tableNumber=${encodeURIComponent(tableNumber)}`,
    )
    if (!res.ok) throw new ApiError(res.status, 'Failed to load table bill')
    return res.json() as Promise<{
      tableNumber: string
      settled: boolean
      totalAmount: number
      orderCount: number
      partySize: number | null
      perPerson: { amount: number; approx: boolean } | null
      checkoutRequested: boolean
      cashPending: boolean
      // #1529 自動のお通し・席料 (サーバが正)。✕ で外すと registerTableExtraOptout → 再取得
      autoExtras: { presetId: string; name: string; price: number; quantity: number }[]
      autoExtrasTotal: number
      orders: {
        id: string; totalAmount: number
        items: { name: string; quantity: number; price: number; variations?: VariationSnapshot[] | null }[]
      }[]
      // #2039 時間料金の状態 (時間制でなければ null)。running の間は支払いを受けない
      timeCharge?: import('../../store/useStore').TimeChargeState | null
    }>
  },

  // #1529 この卓だけ自動のお通し・席料を外す/戻す (客が断った等)。卓セッションに保存
  registerTableExtraOptout: async (storeId: string, tableNumber: string, presetId: string, excluded: boolean) => {
    const res = await apiFetch('/register/table-extra-optout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber, presetId, excluded }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update table extras')
    return res.json() as Promise<{ ok: boolean }>
  },

  // #1529 discount/extras: 卓会計の値引きとお通し・席料。
  // サーバが 請求額 = 卓の合計 + extras − 値引き を確定する
  // #2162 method: 'cash' (既定) / 'store_terminal' (店の端末 — 他社の決済端末で受けた記録だけ)
  registerCashReceive: async (
    storeId: string, tableNumber: string,
    discount?: { discountJpy: number; discountName?: string },
    extras?: { name: string; price: number; quantity: number }[],
    method: RegisterPayMethod = 'cash',
  ) => {
    const res = await apiFetch('/register/cash-receive', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, tableNumber, ...(discount ?? {}), ...(extras ? { extras } : {}), method }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to receive cash')
    return res.json() as Promise<{ paid: string[]; transactionId: string; amount: number }>
  },

  // #1181 FB: 金額入力式の現金受領 (注文に紐付かない単発会計 — S700 amount-charge の現金版)。
  // 会計のみの店 (お土産店含む) の現金売上の記録 + レシート自動印刷の受け皿。
  registerAmountCash: async (
    storeId: string, amount: number, idempotencyKey: string, memo?: string,
    method: RegisterPayMethod = 'cash',
  ) => {
    const res = await apiFetch('/register/amount-cash', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, amount, memo, idempotencyKey, method }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to record cash sale')
    return res.json() as Promise<{ transactionId: string; amount: number }>
  },

  registerBillSlipsPending: async (storeId: string) => {
    const res = await apiFetch(`/register/bill-slips/pending?storeId=${storeId}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to list bill slips')
    return res.json() as Promise<{
      checkouts: { id: string; tableNumber: string; amount: number; requestedAt?: string; source?: string }[]
    }>
  },

  registerBillSlipPreview: async (storeId: string, tableNumber: string) => {
    const res = await apiFetch(
      `/register/bill-slip/preview?storeId=${storeId}&tableNumber=${encodeURIComponent(tableNumber)}`,
    )
    if (!res.ok) throw new ApiError(res.status, 'Failed to get bill slip')
    return res.json() as Promise<{
      tableNumber: string
      preview: string
      qrData: string
      escposBase64: string
    }>
  },

  // ---- Stripe Terminal (S700 店頭リーダー) #1173 ----
  // 登録/解除は OWNER_OR_MANAGER、会計系は全ロール (レジ業務)。apiFetch が
  // オーナー Firebase token or スタッフセッションを自動添付する。

  terminalGetReader: async (storeId: string) => {
    const res = await apiFetch(`/stripe/terminal/reader?storeId=${storeId}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to get terminal reader')
    return res.json() as Promise<{
      registered: boolean
      readerId?: string
      label?: string
      status?: 'online' | 'offline' | 'unknown'
    }>
  },

  terminalRegisterReader: async (storeId: string, registrationCode: string, label?: string) => {
    const res = await apiFetch('/stripe/terminal/readers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, registrationCode, label }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to register terminal reader')
    return res.json() as Promise<{ readerId: string; label: string; status: string }>
  },

  terminalUnregisterReader: async (storeId: string) => {
    const res = await apiFetch(`/stripe/terminal/readers?storeId=${storeId}`, { method: 'DELETE' })
    if (!res.ok) throw new ApiError(res.status, 'Failed to unregister terminal reader')
    return res.json()
  },

  // #1529 discount/extras: 卓会計の値引きとお通し・席料。
  // サーバが 合計 + extras − 値引き で PI を作る
  terminalTableCharge: async (
    storeId: string, tableNumber: string,
    discount?: { discountJpy: number; discountName?: string },
    extras?: { name: string; price: number; quantity: number }[],
  ) => {
    const res = await apiFetch('/stripe/terminal/table-charge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // #2188 screenAcks: この画面は結果を受け取ったら /ack を送る (TerminalChargeModal)
      body: JSON.stringify({ storeId, tableNumber, ...(discount ?? {}), ...(extras ? { extras } : {}), screenAcks: true }),
    })
    return res.json() as Promise<{ paymentIntentId: string; amount: number; orderCount?: number; resumed?: boolean; unseen?: boolean }>
  },

  // #1205 接続テスト: 決済を作らず S700 画面に表示を出して疎通確認 (テスト印刷相当)。
  terminalTestDisplay: async (storeId: string) => {
    const res = await apiFetch('/stripe/terminal/test-display', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId }),
    })
    return res.json() as Promise<{ displayed: boolean; readerId: string }>
  },

  terminalTestDisplayClear: async (storeId: string) => {
    const res = await apiFetch('/stripe/terminal/test-display/clear', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId }),
    })
    return res.json() as Promise<{ cleared: boolean }>
  },

  // #1183: items (物販カート) 指定時はサーバが金額を再計算する (amount は見込み額)。
  terminalAmountCharge: async (
    storeId: string,
    amount: number,
    items?: { itemId: string; quantity: number }[],
    memo?: string,
    // #1392 メニュー会計カート (飲食)。物販の items と別フィールドなのは、
    // 一緒にすると飲食の売上が物販として集計されるため (backend で flow を分ける)。
    menuItems?: MenuSaleLine[],
    // #2031 客の受付番号から読み込んだカート。現金と同じ規則 (番号を出した時点の選択と追加料金) で
    // 値付けするために渡す。
    counterRequestCode?: string,
  ) => {
    const res = await apiFetch('/stripe/terminal/amount-charge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // #2188 screenAcks: この画面は結果を受け取ったら /ack を送る (TerminalChargeModal)
      body: JSON.stringify({ storeId, amount, memo, items, menuItems, counterRequestCode, screenAcks: true }),
    })
    return res.json() as Promise<{ paymentIntentId: string; amount: number; resumed?: boolean; unseen?: boolean }>
  },

  // #1392 メニュー会計の現金受領 — 明細つき。金額はサーバが menu 価格で再計算する
  // (自由入力行のみクライアント値、上限チェックあり)。在庫には触らない。
  /** #1402 counterRequestCode: 客の受付番号から会計した場合。会計が通った時点で
   *  注文化され、呼び出し番号・受け取り状況ページまで既存レールに乗る。 */
  /** #1402 客の受付番号を読んでレジのカートに載せる。読んだ時点で取り込み済みになる
   *  (レジ 2 台で同じ番号を読んでも注文が 2 つできない)。 */
  readCounterRequest: async (storeId: string, code: string) => {
    const res = await apiFetch(
      `/register/counter-request?storeId=${storeId}&code=${encodeURIComponent(code)}`,
    )
    return res.json() as Promise<{
      code: string
      items: {
        menuItemId?: string; name: string; price: number; quantity: number
        lineKey?: string; variations?: VariationSnapshot[]
      }[]
      totalAmount: number
      notes?: string | null
      diningLocation?: string | null
    }>
  },

  /** #1402 会計をやめた時に取り込みを戻す (客が読み直せるように)。 */
  releaseCounterRequest: async (storeId: string, code: string) => {
    const res = await apiFetch('/register/counter-request/release', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, code }),
    })
    return res.json()
  },

  registerMenuCash: async (
    storeId: string,
    items: MenuSaleLine[],
    idempotencyKey: string,
    memo?: string,
    counterRequestCode?: string,
    method: RegisterPayMethod = 'cash',
  ) => {
    const res = await apiFetch('/register/menu-cash', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, items, idempotencyKey, memo, counterRequestCode, method }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to record menu cash sale')
    return res.json() as Promise<{
      transactionId: string
      amount: number
      items: { itemId: string | null; name: string; price: number; quantity: number }[]
      /** #2048 客の受付番号から会計したときに出来た注文。 */
      order?: { id: string; callNumber?: string | null } | null
      counterOrderState?: CounterOrderState | null
    }>
  },

  /** #2061 会計済みで注文がまだの受付番号の会計を、注文にする (再試行)。**金は動かない。**
   *  取引の ID だけで呼べるので、再読み込みの後でも別の端末からでも作れる。 */
  settleCounterOrder: async (storeId: string, transactionId: string) => {
    const res = await apiFetch('/register/counter-request/settle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storeId, transactionId }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to settle the counter order')
    return res.json() as Promise<{
      counterOrderState: CounterOrderState
      order: { id: string; callNumber?: string | null } | null
    }>
  },

  /** #2061 会計済みで注文がまだの会計。レジを開いたときに出す。 */
  listUnsettledCounterOrders: async (storeId: string) => {
    const res = await apiFetch(`/register/counter-request/unsettled?storeId=${encodeURIComponent(storeId)}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to list unsettled counter orders')
    return res.json() as Promise<{ transactions: UnsettledCounterOrder[] }>
  },

  terminalPaymentStatus: async (storeId: string, paymentIntentId: string) => {
    const res = await apiFetch(`/stripe/terminal/payments/${paymentIntentId}?storeId=${storeId}`)
    if (!res.ok) throw new ApiError(res.status, 'Failed to get terminal payment status')
    return res.json() as Promise<{
      status: 'processing' | 'succeeded' | 'failed' | 'canceled'
      transactionId?: string
      failureCode?: string
      /** #2048 客の受付番号の会計が、注文になったか (番号なしの会計には付かない)。 */
      counterOrderState?: CounterOrderState
      counterOrder?: { id: string; callNumber?: string | null }
    }>
  },

  terminalCancelPayment: async (storeId: string, paymentIntentId: string) => {
    const res = await apiFetch(
      `/stripe/terminal/payments/${paymentIntentId}/cancel?storeId=${storeId}`,
      { method: 'POST' },
    )
    return res.json() as Promise<{ status: string }>
  },
  /**
   * #2188 この決済の結果を画面が受け取ったと知らせる。送るまでは、同じ端末の次の会計が
   * 「直前の決済が成立していた」で止まる (backend の `awaitingStaff`)。
   */
  terminalAckPayment: async (storeId: string, paymentIntentId: string) => {
    const res = await apiFetch(
      `/stripe/terminal/payments/${paymentIntentId}/ack?storeId=${storeId}`,
      { method: 'POST' },
    )
    return res.json() as Promise<{ ok: boolean }>
  },

  terminalRetryPayment: async (storeId: string, paymentIntentId: string) => {
    const res = await apiFetch(
      `/stripe/terminal/payments/${paymentIntentId}/retry?storeId=${storeId}`,
      { method: 'POST' },
    )
    return res.json() as Promise<{ status: string }>
  },

  // #439 / #1662: **この店舗だけ**の売上取消請求。/store/disputes/:storeId と
  // 店舗ダッシュボードのバッジが使う。全店舗横断は ownerDisputes が別にある。
  // disputes は #394 で記録、#439 で paymentIntentId からの逆引きで storeId が付いた。
  storeAuthStoreDisputes: async (idToken: string, storeId: string) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/stores/${storeId}/disputes`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to get store disputes')
    return res.json() as Promise<{ disputes: Array<Record<string, unknown>> }>
  },

  storeAuthUpdateProfile: async (idToken: string, ownerName: string, companyName: string) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ ownerName, companyName }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to update profile')
    return res.json()
  },

  // #995 返金は owner|manager。apiFetch でオーナー or スタッフ(店長)トークンを自動添付。
  // #2062 cancelCounterOrder: 全額返金のとき、その会計の店頭注文 (まだ渡していないもの) も
  // 一緒にキャンセルする。サーバは一部返金・渡し済みでは何もしない。
  storeAuthRefund: async (transactionId: string, amount: number, reason: 'customer' | 'store' = 'customer', refundFee: number = 0, method: 'stripe' | 'cash' | 'store_terminal' = 'stripe', cancelCounterOrder: boolean = false) => {
    const res = await apiFetch(`/store-auth/refund`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        transaction_id: transactionId, amount, reason, refund_fee: refundFee, method,
        cancel_counter_order: cancelCounterOrder,
      }),
    })
    if (!res.ok) throw new ApiError(res.status, 'Refund failed')
    return res.json() as Promise<{
      status: string; refundId: string; refundedAmount: number; originalAmount: number
      /** #2062 注文をどうしたか。チェックを外した・一部返金・注文の無い会計は null。 */
      counterOrder?: 'cancelled' | 'handed_over' | 'already_cancelled' | 'none' | 'failed' | null
    }>
  },

  storeAuthDeleteAccount: async (idToken: string) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/me`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to delete account')
    return res.json()
  },

  // #533: 全店舗にまたがる「要対応返金」 (Stripe failed/canceled かつ altRefund 未設定) 一覧
  storeAuthGetFailedRefunds: async (idToken: string) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/refund/failed`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to get failed refunds')
    return res.json() as Promise<{ transactions: Array<Record<string, unknown>> }>
  },

  // #533: カード返金失敗を店舗が代替手段で返金した記録
  storeAuthMarkAlternateRefund: async (
    idToken: string,
    transactionId: string,
    method: 'cash' | 'bank_transfer' | 'other',
    note?: string,
  ) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/refund/mark-alternate`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ transaction_id: transactionId, method, note }),
    })
    if (!res.ok) {
      // #2308 断る理由 (use_refund_voucher / payment_disputed) を画面に渡す
      const body = await res.json().catch(() => ({})) as { detail?: unknown }
      const detail = body.detail && typeof body.detail === 'object' ? body.detail as Record<string, unknown> : undefined
      const code = typeof detail?.code === 'string' ? detail.code : undefined
      throw new ApiError(res.status, 'Failed to mark alternate refund', code, undefined, detail)
    }
    return res.json() as Promise<{ status: string; transactionId: string; method: string }>
  },

  // Stripe Connect
  stripeConnectOnboarding: async (idToken: string) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/connect/onboarding`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to create Connect onboarding')
    return res.json()
  },

  stripeConnectStatus: async (idToken: string) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/connect/status`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to get Connect status')
    return res.json()
  },

  /**
   * #1431 本人確認をせず「現金のみ」で始める / やめる。
   *
   * KYC を理由に店舗が入口で止まり、実証実験が始まらない方が損失が大きい。
   * まず使ってもらい、あとからカード決済 (= 手数料) へ移ってもらう。
   */
  startCashOnlyOwner: async (idToken: string) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/cash-only`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to start cash-only mode')
    return res.json()
  },

  stopCashOnlyOwner: async (idToken: string) => {
    const res = await trackedFetch(`${API_BASE}/store-auth/cash-only`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to leave cash-only mode')
    return res.json()
  },

  /**
   * Issue Stripe AccountSession client_secret for Embedded Components (#460).
   *
   * Custom Connect (stripe_dashboard.type="none") では create_login_link が
   * 使えないため、店舗オーナー UI は Stripe Embedded Components を YOKOSO PAY
   * app に埋め込む形で提供する。本関数はそのための AccountSession を発行する。
   *
   * 返却された client_secret は `@stripe/connect-js` の loadConnectAndInitialize
   * に渡して `<ConnectAccountOnboarding>` / `<ConnectPaymentDetails>` などの
   * Stripe 製 React component に使う。
   */
  /**
   * #460: オーナーの全店舗にまたがる dispute 一覧を取得する。
   * 各 dispute レコードには storeId に加えて storeName が含まれる。
   * `/store/disputes` overview ページで使用。
   */
  // #1656: resultsSeenAt = 前回この一覧を開いた時刻。ダッシュボードの結果バナーは
  // これ以降にクローズした dispute だけを出す (既読なら消える)
  ownerDisputes: async (idToken: string): Promise<{ disputes: Array<Record<string, unknown>>; resultsSeenAt?: string | null }> => {
    const res = await trackedFetch(`${API_BASE}/store-auth/disputes`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to fetch owner disputes')
    return res.json()
  },

  /**
   * 売上取消請求の一覧を開いた = 結果を確認した、として既読時刻を記録する。
   *
   * ownerDisputes (GET) では既読にしない — ダッシュボードのバナー自身が
   * その GET を叩くため、そこで既読を打つとバナーは 1 回描画しただけで消え、
   * 店舗は売上が取り消されたことに気付けない (#1656 / #1663)。
   */
  markOwnerDisputesSeen: async (idToken: string): Promise<void> => {
    const res = await trackedFetch(`${API_BASE}/store-auth/disputes/seen`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to mark disputes seen')
  },

  stripeConnectAccountSession: async (idToken: string): Promise<{ client_secret: string }> => {
    const res = await trackedFetch(`${API_BASE}/store-auth/connect/account-session`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Failed to create account session')
    return res.json()
  },

  // Store Registration (authenticated — store owner only)
  createStoreAuth: async (idToken: string, data: StoreRegistrationPayload) => {
    const res = await trackedFetch(`${API_BASE}/store-register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new ApiError(res.status, 'Store creation failed')
    return res.json()
  },

  deleteStoreAuth: async (idToken: string, storeId: string) => {
    const res = await trackedFetch(`${API_BASE}/store-register/${storeId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${idToken}` },
    })
    if (!res.ok) throw new ApiError(res.status, 'Store deletion failed')
    return res.json()
  },

  updateStoreAuth: async (idToken: string, storeId: string, data: StoreRegistrationPayload) => {
    const res = await trackedFetch(`${API_BASE}/store-register/${storeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new ApiError(res.status, 'Store update failed')
    return res.json()
  },

  reverseGeocode: async (lat: number, lng: number): Promise<{ prefecture: string | null; city: string | null; town: string | null }> => {
    const res = await trackedFetch(`${API_BASE}/geo/reverse?lat=${lat}&lng=${lng}`)
    if (!res.ok) throw new ApiError(res.status, 'Reverse geocoding failed')
    return res.json()
  },

  // #697: 住所 → lat/lng (forward geocode)。バックエンドが GSI AddressSearch
  // をプロキシ。候補なし時は全 null を返すので caller は ピン位置維持で fallback。
  forwardGeocode: async (q: string): Promise<{ lat: number | null; lng: number | null; normalized_address: string | null }> => {
    const res = await trackedFetch(`${API_BASE}/geo/forward?q=${encodeURIComponent(q)}`)
    if (!res.ok) throw new ApiError(res.status, 'Forward geocoding failed')
    return res.json()
  },

  // #78 Step 2: Extract menu items from a photo. Backend wraps Gemini 2.5
  // Flash Vision; caller handles 429/503 for honest error UX (no mock fallback).
  // #344: storeId + auth token required so the backend can attribute
  // the call for #52 quota tracking and refuse cross-store usage.
  // #994: メニュー取込は owner|manager。店長は Firebase user を持たないので
  // staff セッショントークンを優先添付する（quota は backend が per-owner 計上）。
  ocrMenu: async (image: File, storeId: string): Promise<{ items: Array<{ name: string; price: number; description?: string }> }> => {
    const form = new FormData()
    form.append('image', image)
    form.append('storeId', storeId)
    const idToken = _staffSessionToken ?? await getFirebaseIdToken()
    const headers: Record<string, string> = {}
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`
    const res = await trackedFetch(`${API_BASE}/ai/ocr-menu`, { method: 'POST', body: form, headers })
    if (res.status === 429) {
      const resetDate = await parseQuotaExceeded(res)
      if (resetDate) {
        showQuotaExceededToast(resetDate)
      }
    }
    if (!res.ok) throw new ApiError(res.status, `OCR failed (${res.status})`)
    const payload = await res.json()
    // #52 surface quota warning toast when >= 80% used.
    maybeShowQuotaWarning(payload?._meta)
    return payload
  },
}
